import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";

// css
import styles from "./AddNewGoalPop.module.scss";
import { useGetDisciplineAreasQuery, useGetGoalContextsQuery, useGetGoalsQuery } from "store/services/utility";
import { Formik } from "formik";
import goalFormSchema from "./Goal.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { cloneDeep } from "lodash";
import LoadingButton from "components/shared/LoadingButton";
import { useCreateGoalMutation, useUpdateGoalMutation } from "store/services/clients/assessmentTreatmentPlan/goals/manageGoals";
import { successMessageToast } from "helpers/common";
import { AppSelect2Field } from "components/shared/FormComponents/AppSelect2Field";
import { GOAL_DESC_WORD_LIMIT } from "config/constants";

// img

const AddNewGoalPop = ({ showPopup, closePopup, assessmentData, data, submitCallback, clientType }) => {
  const editMode = !!data;
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [initialValues, setInitialValues] = useState({
    discipline_id: null,
    area_id: null,
    goal_id: null,
    context_id: null,
    goal_description: "",
    goal_notes: "",
    status: 18,
  });

  const assessmentId = assessmentData?.assesment?.id;
  const disciplineId = assessmentData?.clientPendingCaseload?.clientCaseloadTherapist?.discipline_id;
  const { data: disciplineAreas, isFetching: areasLoading } = useGetDisciplineAreasQuery(disciplineId, { skip: !disciplineId });
  const { data: disciplineAreaGoals, isFetching: goalsLoading } = useGetGoalsQuery({ disciplineId, areaId: selectedArea }, { skip: !disciplineId || !selectedArea });
  const { data: goalContexts, isFetching: contextsLoading } = useGetGoalContextsQuery({ goalId: selectedGoal }, { skip: !selectedGoal });
  const [submitCreateGoal, { isLoading }] = useCreateGoalMutation();
  const [submitUpdateGoal, { isLoading: updateLoading }] = useUpdateGoalMutation();

  const disciplineAreaOptions =
    disciplineAreas?.length > 0
      ? disciplineAreas?.map(area => ({
        value: area.id,
        label: area.name
      }))
      : [];

  const areaGoalsOptions =
    disciplineAreaGoals?.length > 0
      ? disciplineAreaGoals?.map(goal => ({
        value: goal.id,
        label: goal.title
      }))
      : [];

  const goalContextOptions =
    goalContexts?.length > 0
      ? goalContexts?.map(context => ({
        value: context.id,
        label: context.context
      }))
      : [];

  useEffect(() => {
    if (!data) {
      return;
    }
    setInitialValues(prevState => ({
      ...prevState,
      area_id: {
        value: data?.area_id,
        label: data?.assesmentGoalDisciplineArea?.name
      },
      goal_id: {
        value: data?.goal_id,
        label: data?.assesmentGoalDetail?.title
      },
      context_id: {
        value: data?.context_id,
        label: data?.assesmentGoalContext?.context
      },
      goal_description: data?.goal_description || "",
      goal_notes: data?.goal_notes || "",
    }))

    setSelectedArea(data?.area_id);
    setSelectedGoal(data?.goal_id);
  }, [data]);

  useEffect(() => {
    setInitialValues((prevValues) => ({
      ...prevValues,
      discipline_id: disciplineId
    }))
  }, [disciplineId])

  const handleSubmit = async (values) => {
    try {
      // format payload
      let payload = cloneDeep(values);
      payload.area_id = payload.area_id?.value;
      payload.goal_id = payload.goal_id?.value;
      payload.context_id = payload.context_id?.value;

      let response = null;
      if (editMode) {
        response = await submitUpdateGoal({
          goalId: data?.id,
          data: payload,
          clientType
        });
      } else {
        response = await submitCreateGoal({
          assessmentId,
          data: payload,
          clientType
        });
      }

      if (response?.data) {
        successMessageToast(response?.data?.message)
        closePopup();
        if (typeof submitCallback === "function") submitCallback(response?.data);
      }
    } catch (err) {
      console.error("Error creating goal", err);
    }
  }

  return (
    <>
      <Modal
        show={showPopup}
        className={`${styles.AddNewGoalPop}  AddNewGoalPop`}
        onHide={closePopup}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <h6 className="m-0 fw-bold text-dark">{editMode ? "Edit Goal" : "Add Goal"}</h6>
        </Modal.Header>
        <Modal.Body className={`${styles.modalBody} position-relative rounded`}>
          <Formik
            initialValues={initialValues}
            validationSchema={goalFormSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              handleSubmit,
              setFieldValue,
              errors,
              values
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="py-2">
                  <Row>
                    <Col md="6" className="my-2">
                      <AppSelect2Field
                        name="area_id"
                        placeholder="Select Area"
                        classNamePrefix={"khan"}
                        option={disciplineAreaOptions}
                        isLoading={areasLoading}
                        onChange={(selected) => {
                          setSelectedArea(selected?.value);
                          setFieldValue("goal_id", null);
                        }}
                      />
                    </Col>
                    <Col md="6" className="my-2">
                      <AppSelect2Field
                        name="goal_id"
                        placeholder="Select Goal"
                        classNamePrefix={"khan"}
                        option={areaGoalsOptions}
                        isLoading={goalsLoading}
                        onChange={(selected) => {
                          setSelectedGoal(selected?.value);
                          setFieldValue("context_id", null);
                        }}
                      />
                    </Col>
                    <Col md="6" className="my-2">
                      <AppSelect2Field
                        name="context_id"
                        placeholder="Select Context"
                        classNamePrefix={"khan"}
                        option={goalContextOptions}
                        isLoading={contextsLoading}
                      />
                    </Col>
                    <Col md="12" className="my-2">
                      <AppInputField
                        name="goal_description"
                        fieldType={"textarea"}
                        className={`${styles.formControl} form-control rounded-3 bg-white h-auto`}
                        rows={4}
                        placeholder="Add information on what types of prompts or cues were provided to elicit the goal"
                        // wordLimit={GOAL_DESC_WORD_LIMIT}
                      />
                    </Col>
                  </Row>
                </div>
                {/* <div className="my-2 border-top text-center">
              <Button
                style={{ marginTop: -20 }}
                className={`${styles.add} bg-white d-inline-flex align-items-center justify-content-center rounded-pill border`}
                variant="transparent"
              >
                <span className="icn me-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_1_4309)">
                      <path
                        d="M8.35645 0.81543C6.7742 0.81543 5.22748 1.28462 3.91189 2.16367C2.59629 3.04272 1.57091 4.29215 0.965413 5.75396C0.359912 7.21577 0.201485 8.8243 0.510167 10.3762C0.818848 11.928 1.58077 13.3535 2.69959 14.4723C3.81841 15.5911 5.24388 16.353 6.79573 16.6617C8.34758 16.9704 9.95611 16.812 11.4179 16.2065C12.8797 15.601 14.1292 14.5756 15.0082 13.26C15.8873 11.9444 16.3564 10.3977 16.3564 8.81543C16.3542 6.6944 15.5106 4.6609 14.0108 3.16111C12.511 1.66132 10.4775 0.817724 8.35645 0.81543ZM11.0231 9.4821H9.02312V11.4821C9.02312 11.6589 8.95288 11.8285 8.82785 11.9535C8.70283 12.0785 8.53326 12.1488 8.35645 12.1488C8.17964 12.1488 8.01007 12.0785 7.88504 11.9535C7.76002 11.8285 7.68978 11.6589 7.68978 11.4821V9.4821H5.68978C5.51297 9.4821 5.3434 9.41186 5.21838 9.28684C5.09335 9.16181 5.02312 8.99224 5.02312 8.81543C5.02312 8.63862 5.09335 8.46905 5.21838 8.34403C5.3434 8.219 5.51297 8.14876 5.68978 8.14876H7.68978V6.14876C7.68978 5.97195 7.76002 5.80238 7.88504 5.67736C8.01007 5.55233 8.17964 5.4821 8.35645 5.4821C8.53326 5.4821 8.70283 5.55233 8.82785 5.67736C8.95288 5.80238 9.02312 5.97195 9.02312 6.14876V8.14876H11.0231C11.1999 8.14876 11.3695 8.219 11.4945 8.34403C11.6195 8.46905 11.6898 8.63862 11.6898 8.81543C11.6898 8.99224 11.6195 9.16181 11.4945 9.28684C11.3695 9.41186 11.1999 9.4821 11.0231 9.4821Z"
                        fill="#A8A8A8"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1_4309">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.356445 0.81543)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </span>
                Add New Goal
              </Button>
            </div> */}
                <div className="btnWrpper mt-3">
                  <LoadingButton
                    type="submit"
                    className="d-flex align-items-center justify-content-center commonBtn w-100 fw-bold"
                    isLoading={isLoading || updateLoading}
                  >
                    Save
                  </LoadingButton>
                  <Button
                    className="d-flex align-items-center justify-content-center commonBtn w-100 bg-transparent border-0 text-dark fw-normal"
                    onClick={closePopup}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddNewGoalPop;
